import { setConfigurationLocale } from './locale';
import { applicationContext, foundationContext, guessModuleBasedOnRoute } from '@philips-internal/oswald-ui-foundation';
import { setupAuthentication } from './authentication';
import { setupModules } from './modules';
import { setupFilterService } from '../services';
import { AuthenticationService, HistoryService } from '@philips-internal/oswald-ui-core/lib/services';
import { ApplicationContextListenerScope } from '@philips-internal/oswald-ui-constants';
import { Modal } from '@philips-internal/oswald-ui-components';

async function bootstrap(): Promise<void> {

  applicationContext.setTitle('$expression.$1086035');

  foundationContext.setCanEditSector(false);
  foundationContext.setCanEditProfile(false);
  foundationContext.setShowProfile(true);

  setupAuthentication();
  await AuthenticationService.restoreTokenFromStorage()
  setupFilterService();
  setupModules();
  setupBrowserBackNavigation();
  setupGlobalBackArrow();
  setupForceShowNavigationInModals();

  return Promise.all(
    [
      setConfigurationLocale()
    ])
    .then(() => Promise.resolve());

}

function setupBrowserBackNavigation(): void {
  window.addEventListener('popstate', () => {
    const module = guessModuleBasedOnRoute();
    const currentModule = applicationContext.getCurrentModule();

    if (!module || module.id === currentModule?.id) {
      return;
    }
    (applicationContext as any).currentModule = module;
    (applicationContext as any).notify('modules');
  });
}

// Pushes a default back item whenever the navigation stack is empty on an inner route,
// so the header shows the back arrow on screens that don't manage navigation themselves (e.g. filter screens).
function setupGlobalBackArrow(): void {
  const ensureBackItem = (): void => {
    if (applicationContext.getNavigationStackSize() > 0) {
      return;
    }
    if (!HistoryService.hasBeenInitialized()) {
      return;
    }
    const state = HistoryService.getCurrentRouteState() as { from?: string } | undefined;
    if (!state?.from) {
      return;
    }
    const previousPath = state.from;
    applicationContext.pushNavigationItem(() => {
      HistoryService.push(previousPath);
    });
  };

  ensureBackItem();
  HistoryService.addListener(() => {
    ensureBackItem();
    return Promise.resolve();
  });
  applicationContext.addListener(ApplicationContextListenerScope.NAVIGATION, () => {
    ensureBackItem();
  });
}

type ModalPrototype = {
  notifyApplicationBar: (eventName: string) => void;
  shouldNotifyApplicationBar?: () => boolean;
  __forceShowNavigationPatched?: boolean;
};

// Patches Modal.notifyApplicationBar to always emit `showNavigation: true`,
// preventing non-closeable modals from hiding the header navigation icon on mobile.
function setupForceShowNavigationInModals(): void {
  const modalProto = (Modal as unknown as { prototype: ModalPrototype }).prototype;
  if (!modalProto || modalProto.__forceShowNavigationPatched) {
    return;
  }
  modalProto.__forceShowNavigationPatched = true;

  modalProto.notifyApplicationBar = function notifyApplicationBar(this: ModalPrototype, eventName: string): void {
    if (typeof this.shouldNotifyApplicationBar === 'function' && !this.shouldNotifyApplicationBar()) {
      return;
    }
    const applicationBar = document.querySelector('.c-application-bar');
    const customEvent = new CustomEvent(eventName, { detail: { showNavigation: true } });
    applicationBar?.dispatchEvent(customEvent);
  };
}

export default bootstrap;
